import { Text, Accordion, HStack } from '@chakra-ui/react';
import { ArticleVideoIcon } from 'assets/icons/ArticleVideoIcon';
import { ChevronDownIcon } from 'assets/icons/v2';
import { ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

type LogSectionOpenParams = {
  sectionId?: string;
  sectionTitle: string;
};

type ArticleSectionProps = {
  title: string;
  children: ReactNode;
  hasVideo?: boolean | null;
  id?: string;
  isPreopened?: boolean;
  thinStyle?: boolean;
  trackArticleSectionOpen?: (params: LogSectionOpenParams) => void;
};

export const ArticleSection = ({
  title,
  children,
  hasVideo,
  id,
  isPreopened = false,
  thinStyle = false,
  trackArticleSectionOpen,
}: ArticleSectionProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false); // isOpen only controls the arrow state. Accordion handling gets done by chakra UI

  const hasTrackedOpen = useRef(false);
  const trackOpenOnce = useCallback((): void => {
    if (hasTrackedOpen.current || !trackArticleSectionOpen) return;
    hasTrackedOpen.current = true;
    trackArticleSectionOpen({ sectionId: id, sectionTitle: title });
  }, [id, title, trackArticleSectionOpen]);

  useEffect(() => {
    if (isPreopened) {
      setIsOpen(true);
      trackOpenOnce();
    }
  }, [isPreopened, trackOpenOnce]);

  const toggleAccordion = (): void => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      trackOpenOnce();
    }
  };

  return (
    <Accordion.Root collapsible id={id} defaultValue={isPreopened ? ['0'] : []}>
      <Accordion.Item value="0" bg="background.primary" borderTop="none" px="0" borderBottom="none">
        <Accordion.ItemTrigger
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p="20px 15px"
          position="relative"
          onClick={toggleAccordion}
          borderBottom="default"
          borderColor="border.accent"
          cursor="pointer"
        >
          <HStack gap="15px">
            <Text
              as="h2"
              textStyle={thinStyle ? 'h4.regular' : 'h4.medium'}
              color={isOpen ? 'text.primary' : 'text.primary'}
              textAlign="left"
            >
              {title}
            </Text>
            {hasVideo && <ArticleVideoIcon color="icon.info" boxSize="24px" />}
          </HStack>
          <Accordion.ItemIndicator boxSize="24px" display="flex" alignItems="center">
            {/* TODO replace by proper recipe which styles the indicator */}
            <ChevronDownIcon color={isOpen ? 'icon.primary' : 'text.secondary'} boxSize="24px" />
          </Accordion.ItemIndicator>
        </Accordion.ItemTrigger>
        <Accordion.ItemContent px="l">
          <Accordion.ItemBody
            textStyle="body.m.regular"
            color="text.primary"
            p={{ base: '15px 32px', md: '20px' }}
          >
            {children}
          </Accordion.ItemBody>
        </Accordion.ItemContent>
      </Accordion.Item>
    </Accordion.Root>
  );
};
